import { Suspense } from 'react';
import noInternetAnimation from '../../../assets/lottie/noInternetAnimation.json';
import { LazyLottie } from '../LottieAnimation/lazyLottie';
import { useNoInternet } from './useNoInternet';
import { Button } from '../Buttons';

const NO_INTERNET_ANIMATION_STYLE = {
  height: '20%',
  width: '20.5%', // Maintains 160:128 ratio (5:4) - width = height * 1.25
  aspectRatio: '5/4',
};

export type NoInternetProps = {
  children: React.ReactNode;
};

/**
 * NoInternet component - Monitors network status and displays no-internet UI when offline
 * When online, renders children normally
 */
const NoInternet: React.FC<NoInternetProps> = ({ children }) => {
  const { isOnline, handleRetry } = useNoInternet();
  // If online, render children normally
  if (isOnline) {
    return <>{children}</>;
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
      {noInternetAnimation ? (
        // Lottie is preloaded in the background while online (see App), so it
        // is already in memory when the connection drops
        <Suspense fallback={<div style={NO_INTERNET_ANIMATION_STYLE} />}>
          <LazyLottie
            animationData={noInternetAnimation}
            loop
            style={NO_INTERNET_ANIMATION_STYLE}
          />
        </Suspense>
      ) : (
        <div className="flex items-center justify-center">
          <div className="text-gray-400">Ładowanie...</div>
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-5">
        <div className="flex flex-col items-center justify-center gap-1">
          <p className="text-titleMedium text-on-surface-variant">
            Brak połączenia z internetem
          </p>
          <p className="text-bodyLarge text-on-surface-variant">
            Sprawdź połączenie z internetem i spróbuj ponownie.
          </p>
        </div>
        <Button
          className="cursor-pointer"
          label="Spróbuj ponownie"
          onClick={handleRetry}
        />
      </div>
    </div>
  );
};

export default NoInternet;
